import { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useEventCallback } from '../../hooks/use-event-callback';
import { usePrevious } from '../../hooks/usePrevious';
import { useCastContext } from '../cast-context';
import { CastMediaSession, CastMediaStatus, CastSession } from '../interfaces';
import { getMedia, getMediaStatus } from '../utils/media';

export interface MediaSessionResult {
  media?: CastMediaSession;
  mediaStatus: CastMediaStatus;
}

export function useCastMedia(session: CastSession | undefined): MediaSessionResult {
  const { sender } = useCastContext();

  const [media, setMedia] = useState<CastMediaSession | undefined>(() => getMedia(session));
  const [mediaStatus, setMediaStatus] = useState<CastMediaStatus>(() => getMediaStatus(media, undefined));
  const previousMediaStatus = usePrevious(mediaStatus);

  const handleMount = useEventCallback(() => {
    if (sender) {
      const currentMedia = getMedia(session);

      setMedia(currentMedia);
      setMediaStatus(getMediaStatus(currentMedia, previousMediaStatus));
    }
  });

  const handleMediaSession = useEventCallback((event: cast.framework.MediaSessionEventData) => {
    setMedia(event.mediaSession as CastMediaSession);
    const status = getMediaStatus(event.mediaSession.playerState, previousMediaStatus);
    if (status === 'idle' && event.mediaSession.idleReason === 'ERROR') {
      setMediaStatus('error');
    } else {
      setMediaStatus(status);
    }
  });

  const handleMediaUpdate = useEventCallback((isAlive: boolean) => {
    const mediaSession = getMedia(session);
    if (isAlive && mediaSession) {
      setMediaStatus(getMediaStatus(mediaSession.playerState, previousMediaStatus));
    }
  });

  const batchedMount = useEventCallback(() => {
    unstable_batchedUpdates(() => {
      handleMount();
    });
  });

  const batchedMediaSession = useEventCallback((event: cast.framework.MediaSessionEventData) => {
    unstable_batchedUpdates(() => {
      handleMediaSession(event);
    });
  });

  useEffect(() => {
    batchedMount();
  }, [batchedMount]);

  useEffect(() => {
    if (sender && session) {
      session.addEventListener(sender.SessionEventType.MEDIA_SESSION, batchedMediaSession);

      return () => {
        session.removeEventListener(sender.SessionEventType.MEDIA_SESSION, batchedMediaSession);
      };
    }
  }, [batchedMediaSession, sender, session]);

  useEffect(() => {
    if (media) {
      media.addUpdateListener(handleMediaUpdate);

      return () => {
        media.removeUpdateListener(handleMediaUpdate);
      };
    }
  }, [handleMediaUpdate, media]);

  return {
    media,
    mediaStatus,
  } as const;
}
